import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Beaker, Syringe, AlertTriangle } from "lucide-react";
import ScrambleText from "./ScrambleText";
import { buzz } from "../lib/haptics";

const SYRINGES = [
  { label: "0.3 mL", ml: 0.3, maxUnits: 30, majorStep: 5 },
  { label: "0.5 mL", ml: 0.5, maxUnits: 50, majorStep: 10 },
  { label: "1 mL", ml: 1, maxUnits: 100, majorStep: 10 },
];

function Stepper({ label, unit, value, setValue, step, min, max, testId }) {
  const change = (dir) => {
    setValue((v) => {
      const next = Math.min(max, Math.max(min, +(v + dir * step).toFixed(3)));
      return next;
    });
    buzz(10);
  };
  return (
    <div className="rounded-xl border border-[#1c1c1c] bg-[#0c0c0c] p-4">
      <div className="font-mono2 mb-3 text-[10px] uppercase tracking-[0.2em] text-white/45">
        {label}
      </div>
      <div className="flex items-center justify-between gap-3">
        <button
          data-testid={`${testId}-minus`}
          onClick={() => change(-1)}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-[#2a2a2a] text-white/70 transition-colors hover:border-[#d91616] hover:text-[#d91616] active:scale-95"
          aria-label={`decrease ${label}`}
        >
          <Minus size={16} />
        </button>
        <div className="flex flex-1 items-baseline justify-center gap-1.5">
          <input
            data-testid={testId}
            type="number"
            value={value}
            onChange={(e) => setValue(Math.min(max, Math.max(min, Number(e.target.value) || 0)))}
            className="w-full min-w-0 bg-transparent text-center font-display text-3xl font-black text-white outline-none"
          />
          <span className="font-mono2 text-xs text-white/40">{unit}</span>
        </div>
        <button
          data-testid={`${testId}-plus`}
          onClick={() => change(1)}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-[#2a2a2a] text-white/70 transition-colors hover:border-[#d91616] hover:text-[#d91616] active:scale-95"
          aria-label={`increase ${label}`}
        >
          <Plus size={16} />
        </button>
      </div>
    </div>
  );
}

export default function DoseCalculator() {
  const [peptideMg, setPeptideMg] = useState(10);
  const [waterMl, setWaterMl] = useState(2);
  const [doseMcg, setDoseMcg] = useState(250);
  const [syringe, setSyringe] = useState(SYRINGES[2]);

  const { units, drawMl, concentration, dosesPerVial, fill, overflow } = useMemo(() => {
    const conc = waterMl > 0 ? peptideMg / waterMl : 0; // mg per mL
    const doseMg = doseMcg / 1000;
    const draw = conc > 0 ? doseMg / conc : 0; // mL to draw
    const iu = draw * 100; // U-100 insulin units
    const doses = doseMcg > 0 ? (peptideMg * 1000) / doseMcg : 0;
    return {
      units: iu,
      drawMl: draw,
      concentration: conc,
      dosesPerVial: doses,
      fill: Math.max(0, Math.min(1, iu / syringe.maxUnits)),
      overflow: iu > syringe.maxUnits,
    };
  }, [peptideMg, waterMl, doseMcg, syringe]);

  const tickCount = Math.floor(syringe.maxUnits / syringe.majorStep) + 1;

  return (
    <section
      id="lab"
      data-testid="lab"
      className="relative overflow-hidden bg-[#070707] py-28 md:py-40"
    >
      <span className="ambient-red" style={{ width: "40rem", height: "40rem", top: "-8rem", left: "-12rem" }} aria-hidden="true" />
      <span className="section-kanji font-jp bottom-6 right-4 text-[11rem] md:text-[18rem]" aria-hidden="true">
        量
      </span>

      <div className="relative z-10 mx-auto max-w-[1100px] px-6 md:px-12">
        <div className="mb-12 md:mb-16">
          <ScrambleText
            as="span"
            text="03 — THE LAB"
            className="font-mono2 mb-4 block text-xs uppercase tracking-[0.4em] text-[#d91616]"
          />
          <h2 className="font-display text-5xl font-black leading-[0.9] tracking-tighter md:text-7xl">
            RECONSTITUTION
            <br />
            <span className="text-stroke-red">CALCULATOR.</span>
          </h2>
          <p className="mt-6 max-w-xl font-body text-sm leading-relaxed text-white/55 md:text-base">
            Precision is everything. Enter your vial and dosing plan — we'll compute the
            exact draw on a standard U-100 insulin syringe.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Inputs */}
          <div className="space-y-4">
            <div className="mb-2 flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/40">
              <Beaker size={13} className="text-[#d91616]" /> Your setup
            </div>
            <Stepper label="Peptide per vial" unit="mg" value={peptideMg} setValue={setPeptideMg} step={1} min={1} max={100} testId="calc-peptide" />
            <Stepper label="Bacteriostatic water" unit="mL" value={waterMl} setValue={setWaterMl} step={0.5} min={0.5} max={10} testId="calc-water" />
            <Stepper label="Desired dose" unit="mcg" value={doseMcg} setValue={setDoseMcg} step={50} min={50} max={5000} testId="calc-dose" />

            <div className="rounded-xl border border-[#1c1c1c] bg-[#0c0c0c] p-4">
              <div className="font-mono2 mb-3 text-[10px] uppercase tracking-[0.2em] text-white/45">
                Syringe size
              </div>
              <div className="grid grid-cols-3 gap-2" role="group" aria-label="syringe size">
                {SYRINGES.map((s) => (
                  <button
                    key={s.ml}
                    data-testid={`calc-syringe-${s.ml}`}
                    onClick={() => {
                      setSyringe(s);
                      buzz(10);
                    }}
                    className={`rounded-lg border py-3 font-display text-sm font-black transition-colors active:scale-95 ${
                      syringe.ml === s.ml
                        ? "border-[#d91616] bg-[#d91616] text-white"
                        : "border-[#2a2a2a] text-white/70 hover:border-[#d91616] hover:text-[#d91616]"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Result + syringe */}
          <div className="flex flex-col justify-between rounded-2xl border border-[#d91616]/25 bg-[#0a0a0a] p-6 shadow-[0_0_70px_-20px_rgba(217,22,22,0.5)] md:p-8">
            <div>
              <div className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/40">
                <Syringe size={13} className="text-[#d91616]" /> Draw to this mark
              </div>
              <div className="mt-3 flex items-end gap-2" data-testid="calc-result-units">
                <span className="font-display text-6xl font-black leading-none text-white md:text-7xl">
                  {units.toFixed(units < 10 ? 1 : 0)}
                </span>
                <span className="font-mono2 mb-2 text-sm uppercase tracking-[0.2em] text-[#d91616]">
                  units
                </span>
              </div>
              <p className="mt-1 font-mono2 text-[11px] text-white/45">
                ({drawMl.toFixed(3)} mL on a {syringe.label} U-100 syringe)
              </p>
              {overflow && (
                <div
                  data-testid="calc-overflow"
                  className="mt-3 flex items-start gap-2 rounded-lg border border-[#d91616]/40 bg-[#d91616]/10 p-2.5 font-mono2 text-[10px] leading-relaxed text-[#ff6b6b]"
                >
                  <AlertTriangle size={13} className="mt-px shrink-0" />
                  Dose exceeds this {syringe.label} syringe ({syringe.maxUnits}u). Split the dose or choose a larger syringe.
                </div>
              )}
            </div>

            {/* Animated syringe with numeric scale */}
            <div className="my-8">
              <svg
                viewBox="0 0 340 104"
                className="w-full overflow-visible"
                role="img"
                aria-label="insulin syringe fill level with unit scale"
                data-testid="syringe-scale"
              >
                {/* barrel */}
                <rect x="44" y="52" width="250" height="26" rx="4" fill="#0d0d0d" stroke="#2a2a2a" />
                {/* fill — grows from the needle (left) side */}
                <motion.rect
                  x="46"
                  y="54"
                  height="22"
                  rx="2"
                  fill={overflow ? "#ff2d2d" : "url(#tojiFill)"}
                  initial={false}
                  animate={{ width: 246 * fill }}
                  transition={{ type: "spring", stiffness: 120, damping: 20 }}
                />
                {/* plunger rod (empty region, right of the stopper) */}
                <motion.line
                  y1="65"
                  x2="298"
                  y2="65"
                  stroke="#3a3a3a"
                  strokeWidth="2"
                  initial={false}
                  animate={{ x1: 46 + 246 * fill }}
                  transition={{ type: "spring", stiffness: 120, damping: 20 }}
                />
                {/* plunger stopper at the fill boundary */}
                <motion.rect
                  y="50"
                  width="4"
                  height="30"
                  rx="1"
                  fill="#ff5555"
                  initial={false}
                  animate={{ x: 45 + 246 * fill }}
                  transition={{ type: "spring", stiffness: 120, damping: 20 }}
                />
                {/* thumb flange */}
                <rect x="298" y="48" width="7" height="34" rx="2" fill="#3a3a3a" />
                {/* needle (left) */}
                <rect x="32" y="60" width="12" height="10" rx="1" fill="#2a2a2a" />
                <rect x="6" y="63.5" width="28" height="3" fill="#4a4a4a" />

                {/* numeric scale above the barrel */}
                {Array.from({ length: tickCount }).map((_, i) => {
                  const x = 46 + (i * 246) / (tickCount - 1);
                  const val = i * syringe.majorStep;
                  return (
                    <g key={i}>
                      <line x1={x} y1="44" x2={x} y2="52" stroke="#3a3a3a" strokeWidth="1" />
                      <text
                        x={x}
                        y="37"
                        textAnchor="middle"
                        fontSize="9"
                        className="font-mono2"
                        fill="rgba(255,255,255,0.55)"
                      >
                        {val}
                      </text>
                    </g>
                  );
                })}

                {/* current-draw pointer below the barrel */}
                <motion.g
                  initial={false}
                  animate={{ x: 246 * fill }}
                  transition={{ type: "spring", stiffness: 120, damping: 20 }}
                >
                  <path d="M46 80 L42 88 L50 88 Z" fill="#d91616" />
                  <text
                    x="46"
                    y="101"
                    textAnchor="middle"
                    fontSize="12"
                    className="font-mono2"
                    fill="#ff5555"
                    fontWeight="700"
                  >
                    {units.toFixed(units < 10 ? 1 : 0)}
                  </text>
                </motion.g>

                <defs>
                  <linearGradient id="tojiFill" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0" stopColor="#7a0000" />
                    <stop offset="1" stopColor="#ff2d2d" />
                  </linearGradient>
                </defs>
              </svg>
              <p className="font-mono2 mt-2 text-center text-[10px] uppercase tracking-[0.2em] text-white/30">
                Scale in insulin units · draw to <span className="text-[#ff5555]">{units.toFixed(units < 10 ? 1 : 0)}</span>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-[#1c1c1c] bg-[#0c0c0c] p-3">
                <div className="font-mono2 text-[9px] uppercase tracking-[0.15em] text-white/40">Concentration</div>
                <div className="mt-1 font-display text-lg font-black text-white" data-testid="calc-concentration">
                  {concentration.toFixed(2)} <span className="text-xs text-white/40">mg/mL</span>
                </div>
              </div>
              <div className="rounded-lg border border-[#1c1c1c] bg-[#0c0c0c] p-3">
                <div className="font-mono2 text-[9px] uppercase tracking-[0.15em] text-white/40">Doses / vial</div>
                <div className="mt-1 font-display text-lg font-black text-white" data-testid="calc-doses">
                  {Math.floor(dosesPerVial)}
                </div>
              </div>
            </div>

            <p className="font-mono2 mt-5 text-[9px] leading-relaxed tracking-wide text-white/30">
              For research reference only. Not medical advice. Always verify calculations
              independently before any laboratory procedure.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
